import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Button, Space, Card, Tag, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';

const { Option } = Select;

// Opções de campos disponíveis para filtro de tags
const fieldOptions = [
  { label: 'Nome', value: 'name', type: 'text' },
  { label: 'Status', value: 'active', type: 'boolean' },
  { label: 'Uso por Clientes', value: 'customers_usage', type: 'usage' },
];

// Operadores por tipo de campo
const operatorsByType = {
  text: [
    { label: 'Contém', value: 'contains' },
    { label: 'Não contém', value: 'not_contains' },
    { label: 'É exatamente', value: 'equals' },
    { label: 'Está em branco', value: 'is_empty' },
    { label: 'Está preenchido', value: 'is_not_empty' },
  ],
  boolean: [
    { label: 'É ativo', value: 'is_active' },
    { label: 'É inativo', value: 'is_inactive' },
    { label: 'Ambos', value: 'both' },
  ],
  usage: [
    { label: 'Tem clientes', value: 'has_customers' },
    { label: 'Não tem clientes', value: 'no_customers' },
    { label: 'Ambos', value: 'both' },
  ],
};

const TagAdvancedFilterModal = ({ open, onCancel, onApply, initialFilters = [] }) => {
  const [form] = Form.useForm();
  const [filters, setFilters] = useState([]);
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    if (open) {
      if (initialFilters.length > 0) {
        // Converter filtros iniciais
        const convertedFilters = initialFilters.map((filter, index) => ({
          id: filter.id || Date.now() + index,
          field: filter.field,
          operator: filter.operator,
          fieldType: filter.fieldType
        }));
        
        setFilters(convertedFilters);
        
        // Preencher formulário
        const initialValues = {};
        initialFilters.forEach((filter, index) => {
          initialValues[`field_${index}`] = filter.field;
          initialValues[`operator_${index}`] = filter.operator;
          initialValues[`value_${index}`] = filter.value;
        });
        
        form.setFieldsValue(initialValues);
        setFormValues(initialValues);
      } else {
        addFilter();
      }
    } else {
      setFilters([]);
      form.resetFields();
      setFormValues({});
    }
  }, [open, initialFilters]);

  const addFilter = () => {
    setFilters(prevFilters => {
      const newFilter = {
        id: Date.now(),
        field: 'name',
        operator: 'contains',
        fieldType: 'text'
      };
      const newFilters = [...prevFilters, newFilter];

      // Inicializar valores padrão no form
      const newIndex = prevFilters.length;
      const defaultValues = {
        [`field_${newIndex}`]: 'name',
        [`operator_${newIndex}`]: 'contains'
      };
      form.setFieldsValue(defaultValues);
      setFormValues(prev => ({ ...prev, ...defaultValues }));

      return newFilters;
    });
  };

  const removeFilter = (filterId) => {
    const filterIndex = filters.findIndex(f => f.id === filterId);
    const newFilters = filters.filter(f => f.id !== filterId);
    setFilters(newFilters);

    // Reorganizar campos do form após remoção
    const currentValues = form.getFieldsValue();
    const newValues = {};

    newFilters.forEach((filter, newIndex) => {
      const oldIndex = filters.findIndex(f => f.id === filter.id);
      if (oldIndex !== -1 && oldIndex !== newIndex) {
        // Mover valores para o novo índice
        Object.keys(currentValues).forEach(key => {
          if (key.includes(`_${oldIndex}`)) {
            const newKey = key.replace(`_${oldIndex}`, `_${newIndex}`);
            newValues[newKey] = currentValues[key];
          }
        });
      } else if (oldIndex === newIndex) {
        // Manter valores no mesmo índice
        Object.keys(currentValues).forEach(key => {
          if (key.includes(`_${oldIndex}`)) {
            newValues[key] = currentValues[key];
          }
        });
      }
    });

    form.setFieldsValue(newValues);
    setFormValues(newValues);
  };

  const handleApply = () => {
    form.validateFields().then(values => {
      const processedFilters = filters.map((filter, index) => {
        const field = values[`field_${index}`];
        const operator = values[`operator_${index}`];
        const value = values[`value_${index}`];
        const fieldConfig = fieldOptions.find(opt => opt.value === field);

        return {
          id: filter.id,
          field,
          operator,
          value,
          fieldType: fieldConfig?.type || 'text'
        };
      }).filter(filter => {
        // Remover filtros incompletos
        if (['is_empty', 'is_not_empty', 'is_active', 'is_inactive', 'both', 'has_customers', 'no_customers'].includes(filter.operator)) {
          return true; // Estes operadores não precisam de valor
        }
        return filter.value !== undefined && filter.value !== null && filter.value !== '';
      });

      onApply(processedFilters);
    }).catch(errorInfo => {
      console.error('Validation Failed:', errorInfo);
    });
  };

  const renderValueInput = (filter, index) => {
    const field = formValues[`field_${index}`] || 'name';
    const operator = formValues[`operator_${index}`] || 'contains';

    // Operadores que não precisam de valor
    if (['is_empty', 'is_not_empty', 'is_active', 'is_inactive', 'both', 'has_customers', 'no_customers'].includes(operator)) {
      return null;
    }

    if (field === 'active' || field === 'customers_usage') {
      // Para campos boolean e usage, os operadores não precisam de valor
      return null;
    }

    return (
      <Form.Item
        name={`value_${index}`}
        label="Valor"
        rules={[{ required: true, message: 'Digite o valor' }]}
      >
        <Input placeholder="Digite o valor para filtrar" />
      </Form.Item>
    );
  };

  const onFormValuesChange = (changedValues, allValues) => {
    setFormValues(allValues);
    
    // Se mudou o campo, resetar o operador para o padrão do tipo
    Object.keys(changedValues).forEach(key => {
      if (key.startsWith('field_')) {
        const index = key.split('_')[1];
        const field = changedValues[key];
        const fieldConfig = fieldOptions.find(opt => opt.value === field);
        const defaultOperator = operatorsByType[fieldConfig?.type || 'text'][0].value;
        
        form.setFieldsValue({
          [`operator_${index}`]: defaultOperator,
          [`value_${index}`]: undefined // Limpar valor quando mudar o campo
        });
        
        // Atualizar o filtro na lista
        setFilters(prevFilters => {
          const newFilters = [...prevFilters];
          if (newFilters[index]) {
            newFilters[index].field = field;
            newFilters[index].operator = defaultOperator;
            newFilters[index].fieldType = fieldConfig?.type || 'text';
          }
          return newFilters;
        });
      }
    });
  };

  const getFilterDescription = (filter, index) => {
    const field = formValues[`field_${index}`] || filter.field || 'name';
    const operator = formValues[`operator_${index}`] || filter.operator || 'contains';
    const value = formValues[`value_${index}`] || filter.value;
    
    const fieldLabel = fieldOptions.find(opt => opt.value === field)?.label || field;
    const fieldConfig = fieldOptions.find(opt => opt.value === field);
    const operatorLabel = operatorsByType[fieldConfig?.type || 'text'].find(opt => opt.value === operator)?.label || operator;

    // Para operadores que não precisam de valor (usage e alguns boolean), mostrar apenas campo e operador
    const operatorsWithoutValue = ['has_customers', 'no_customers', 'both', 'is_active', 'is_inactive', 'is_empty', 'is_not_empty'];
    
    if (operatorsWithoutValue.includes(operator)) {
      return `${fieldLabel}: ${operatorLabel}`;
    }
    
    // Para operadores que precisam de valor, incluir o valor na descrição
    const displayValue = value !== undefined && value !== null && value !== '' ? value : '';
    return displayValue ? `${fieldLabel}: ${operatorLabel} "${displayValue}"` : `${fieldLabel}: ${operatorLabel}`;
  };

  return (
    <Modal
      title="Filtros Avançados de Tags"
      open={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button key="clear" onClick={() => {
          setFilters([]);
          form.resetFields();
          onApply([]);
        }}>
          Limpar Filtros
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply}>
          Aplicar Filtros
        </Button>
      ]}
      width={800}
      maskClosable={false}
      translate="no"
      className="notranslate"
    >
      <Form form={form} layout="vertical" onValuesChange={onFormValuesChange}>
        {filters.map((filter, index) => (
          <Card
            key={filter.id}
            size="small"
            style={{ marginBottom: 16 }}
            title={
              <Space>
                <span>Filtro {index + 1}</span>
                <Tag color="blue">{getFilterDescription(filter, index)}</Tag>
              </Space>
            }
            extra={
              filters.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeFilter(filter.id)}
                >
                  Remover
                </Button>
              )
            }
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={`field_${index}`}
                  label="Campo"
                  initialValue="name"
                  rules={[{ required: true, message: 'Selecione o campo' }]}
                >
                  <Select placeholder="Selecione o campo">
                    {fieldOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={`operator_${index}`}
                  label="Operador"
                  initialValue="contains"
                  rules={[{ required: true, message: 'Selecione o operador' }]}
                >
                  <Select placeholder="Selecione o operador">
                    {(() => {
                      const field = formValues[`field_${index}`] || 'name';
                      const fieldConfig = fieldOptions.find(opt => opt.value === field);
                      const operators = operatorsByType[fieldConfig?.type || 'text'] || operatorsByType.text;
                      return operators.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ));
                    })()}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                {renderValueInput(filter, index)}
              </Col>
            </Row>
          </Card>
        ))}

        <Button
          type="dashed"
          onClick={addFilter}
          icon={<PlusOutlined />}
          style={{ width: '100%', marginTop: 16 }}
        >
          Adicionar Filtro
        </Button>
      </Form>
    </Modal>
  );
};

export default TagAdvancedFilterModal;